// Keuzelijst voor 'Output' in de output-widget van een live rapportage.
//
// Nodig omdat de lijst alleen de titel van de output toonde, en die titel is
// per activiteit vaak dezelfde ('Aantal deelnemers'): niet te zien welke
// output bij welke activiteit hoort. Updateverzoek van Team Sportclub,
// 2026-10-14. Zelfde vorm als buildQuestionChoices ('Vragenlijst · Vraag').

import notDeleted from '../../../../helpers/notDeleted'

/**
 * @typedef {object} OutputChoice
 * @property {string} value - ID van de Output
 * @property {string} label - 'Activiteit · Output'
 */

/**
 * @param {object} a
 * @param {object} b
 * @returns {number} Op Position, daarna ID — zodat een gelijke Position niet
 *   de Firestore-volgorde laat bepalen.
 *   AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
 */
const byPosition = (a, b) => ((Number(a.Position) || 0) - (Number(b.Position) || 0))
    || String(a.ID).localeCompare(String(b.ID))

/**
 * @param {Array<object>} outputs - Outputs van de organisatie
 * @param {Array<object>} activities - Activities van de organisatie, óók die in de prullenbak
 * @returns {OutputChoice[]} Per activiteit in activiteitsvolgorde, daarbinnen op
 *   Position; als laatste de outputs waarvan de activiteit niet (meer) bestaat
 */
const buildOutputChoices = (outputs, activities) => {
    // Ongefilterd binnen: een activiteit in de prullenbak heeft een naam, een
    // activiteit die niet bestaat niet. Hun outputs kunnen nog registraties hebben.
    // AI-CONTEXT: See docs/pitfalls.md#database-hard-to-soft-delete-leaves-old-orphans
    const sortedActivities = [...activities].sort((a, b) => ((Number(a.Position) || 0) - (Number(b.Position) || 0))
        || String(a.Activity || '').localeCompare(String(b.Activity || ''))
        || String(a.ID).localeCompare(String(b.ID)))
    const known = new Set(sortedActivities.map(a => a.ID))
    const living = outputs.filter(notDeleted).sort(byPosition)
    const title = (output) => (output.Title || '').trim() || '(output zonder naam)'
    const activityName = (activity) => `${(activity.Activity || '').trim() || 'Activiteit zonder naam'}`
        + `${notDeleted(activity) ? '' : ' (in prullenbak)'}`

    const grouped = sortedActivities.flatMap(activity => living
        .filter(o => o.ActivityID === activity.ID)
        .map(o => ({ value: o.ID, label: `${activityName(activity)} · ${title(o)}` })))

    const loose = living
        .filter(o => !known.has(o.ActivityID))
        .map(o => ({ value: o.ID, label: `Zonder activiteit · ${title(o)}` }))

    return [...grouped, ...loose]
}

export default buildOutputChoices
